import { useState } from "react";

import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { useTranslation } from "react-i18next";

import classNames from "../../../utils/classNames";
import SearchPaletteDialog from "./SearchPaletteDialog";
import {
  SearchPaletteItem,
  SearchPaletteItemType,
} from "./SearchPaletteTypes";

interface SearchPaletteInputProps {
  label: string;
  placeholder?: string;
  theme?: "light" | "dark";
  type?: SearchPaletteItemType;
  items?: SearchPaletteItem[] | null;
  onSelect?: (item: SearchPaletteItem) => void;
}

export default function SearchPaletteInput({
  label,
  placeholder = "Search...",
  theme = "light",
  type,
  items = null,
  onSelect,
}: SearchPaletteInputProps) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<SearchPaletteItem | null>(null);

  const mode = {
    opener:
      theme === "dark"
        ? "bg-gray-900 text-gray-200 border-gray-700 placeholder:text-gray-400"
        : "bg-white text-gray-900 border-gray-300 placeholder:text-gray-500",
    icon: theme === "dark" ? "text-gray-500" : "text-gray-400",
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-500 mb-2">{label}</label>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={classNames(
          "w-full rounded-md border px-3 py-2 text-left text-sm flex items-center gap-2 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500",
          mode.opener
        )}
      >
        <MagnifyingGlassIcon className={classNames("h-5 w-5 shrink-0", mode.icon)} />
        <span className={classNames("truncate", selected ? "" : "text-gray-500")}>
          {selected?.name ?? t(placeholder)}
        </span>
      </button>

      <SearchPaletteDialog
        open={open}
        onClose={() => setOpen(false)}
        placeholder={placeholder}
        theme={theme}
        type={type}
        items={items}
        onSelect={(item) => {
          setSelected(item);
          onSelect?.(item);
        }}
      />
    </div>
  );
}

export type { SearchPaletteItem, SearchPaletteItemType } from "./SearchPaletteTypes";